import { DateRangePickerProps } from "@amzn/awsui-components-react";
import { getString } from "common/ui-string-labels/ui-strings-utils";

export const START_OF_DAY_SUFFIX = "T00:00:00Z";
export const END_OF_DAY_SUFFIX = "T23:59:59Z";
export const DATE_RANGE_STRING_PATH = "bulkOrders.filters.dateRange";

/** Shared relative date range presets used across all DateRangePicker components */
export const DATE_RANGE_RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] = [
    { key: 'previous-7-days', amount: 7, unit: 'day', type: 'relative' },
    { key: 'previous-14-days', amount: 14, unit: 'day', type: 'relative' },
    { key: 'previous-30-days', amount: 30, unit: 'day', type: 'relative' },
    { key: 'previous-90-days', amount: 90, unit: 'day', type: 'relative' },
];

/** Formats a relative range for display (e.g., { amount: 7, unit: 'day' } → "Last 7 days") */
export const formatRelativeRange = (e: { amount: number; unit: string }): string => {
    const unit = e.amount === 1 ? e.unit : `${e.unit}s`;
    return `Last ${e.amount} ${unit}`;
};

/**
 * Validates that the selected date range is valid (start date is not after end date).
 */
export const validateDateRange = (value: DateRangePickerProps.Value | null): DateRangePickerProps.ValidationResult => {
    if (value?.type === "absolute" && value.startDate && value.endDate) {
        if (new Date(value.startDate) > new Date(value.endDate)) {
            return { valid: false, errorMessage: getString(`${DATE_RANGE_STRING_PATH}.errorInvalidRange`) };
        }
    }
    return { valid: true };
};

/**
 * Converts a DateRangePicker value to the filter format expected by the ITO API.
 * Uses UTC boundaries (Z suffix) for consistent timezone-independent filtering
 * against the `requested.on` field.
 *
 * Supports both absolute and relative ranges.
 *
 * @returns [fromISO, toISO] or null if no filter should be applied
 */
export const buildDateRangeFilter = (dateRangeValue: DateRangePickerProps.Value | null): [string, string] | null => {
    if (!dateRangeValue) return null;

    if (dateRangeValue.type === "absolute") {
        const fromISO = dateRangeValue.startDate
            ? dateRangeValue.startDate + START_OF_DAY_SUFFIX
            : "";
        const toISO = dateRangeValue.endDate
            ? dateRangeValue.endDate + END_OF_DAY_SUFFIX
            : "";
        return [fromISO, toISO];
    }

    if (dateRangeValue.type === "relative") {
        const now = new Date();
        const from = new Date(now);
        switch (dateRangeValue.unit) {
            case 'week':  from.setUTCDate(from.getUTCDate() - dateRangeValue.amount * 7); break;
            case 'month': from.setUTCMonth(from.getUTCMonth() - dateRangeValue.amount); break;
            case 'year':  from.setUTCFullYear(from.getUTCFullYear() - dateRangeValue.amount); break;
            default:      from.setUTCDate(from.getUTCDate() - dateRangeValue.amount); break; // day
        }
        return [
            from.toISOString().split("T")[0] + START_OF_DAY_SUFFIX,
            now.toISOString().split("T")[0] + END_OF_DAY_SUFFIX,
        ];
    }

    return null;
};
